<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import format from 'date-fns/format';

import Spinner from 'shared/components/Spinner.vue';
import InstagramMediaReportsAPI from 'dashboard/api/instagramMediaReports';

const props = defineProps({
  selectedInbox: {
    type: Object,
    default: null,
  },
});

// Form state
const newType = ref('adversario');
const newPlatform = ref('todas');
const newFormat = ref('@handle');
const newValue = ref('');
const isAdding = ref(false);

// Import state
const importText = ref('');
const isImporting = ref(false);

// Map entries state
const mapEntries = ref([]);
const totalCount = ref(0);
const isLoading = ref(false);
const currentPage = ref(1);
const perPage = ref(25);
const searchQuery = ref('');

// Computed
const filteredEntries = computed(() => {
  if (!searchQuery.value.trim()) return mapEntries.value;
  const q = searchQuery.value.toLowerCase();
  return mapEntries.value.filter(
    e =>
      (e.value && e.value.toLowerCase().includes(q)) ||
      (e.source && e.source.toLowerCase().includes(q))
  );
});

const totalPages = computed(() => {
  return Math.max(1, Math.ceil(totalCount.value / perPage.value));
});

// Helper functions
function idPrefix(entry) {
  if (entry.entry_type === 'term') return 't';
  if (entry.platform === 'instagram') return 'ig';
  return 'rd';
}

function formatDate(dateStr) {
  try {
    return format(new Date(dateStr), 'dd/MM/yyyy');
  } catch {
    return dateStr;
  }
}

// API functions
async function fetchMapEntries() {
  if (!props.selectedInbox) return;
  isLoading.value = true;
  try {
    const response = await InstagramMediaReportsAPI.getPoliticalMapEntries({
      inboxId: props.selectedInbox.id,
      page: currentPage.value,
    });
    mapEntries.value = response.data.entries || [];
    totalCount.value = response.data.total_count || 0;
  } catch {
    mapEntries.value = [];
    totalCount.value = 0;
  } finally {
    isLoading.value = false;
  }
}

async function addEntry() {
  if (!props.selectedInbox || !newValue.value.trim()) return;
  isAdding.value = true;
  try {
    await InstagramMediaReportsAPI.createPoliticalMapEntry({
      inboxId: props.selectedInbox.id,
      entryType: newType.value,
      platform: newPlatform.value,
      entryFormat: newFormat.value,
      value: newValue.value.trim(),
    });
    newValue.value = '';
    fetchMapEntries();
  } catch {
    // silently fail
  } finally {
    isAdding.value = false;
  }
}

async function removeEntry(entryId) {
  if (!props.selectedInbox) return;
  try {
    await InstagramMediaReportsAPI.deletePoliticalMapEntry({
      inboxId: props.selectedInbox.id,
      entryId,
    });
    fetchMapEntries();
  } catch {
    // silently fail
  }
}

async function validateEntry(entryId) {
  if (!props.selectedInbox) return;
  try {
    await InstagramMediaReportsAPI.validatePoliticalMapEntry({
      inboxId: props.selectedInbox.id,
      entryId,
    });
    fetchMapEntries();
  } catch {
    // silently fail
  }
}

async function generateSuggestions() {
  if (!props.selectedInbox) return;
  try {
    await InstagramMediaReportsAPI.generatePoliticalSuggestions({
      inboxId: props.selectedInbox.id,
    });
    fetchMapEntries();
  } catch {
    // silently fail
  }
}

async function saveContext() {
  if (!props.selectedInbox) return;
  try {
    await InstagramMediaReportsAPI.savePoliticalContext({
      inboxId: props.selectedInbox.id,
    });
  } catch {
    // silently fail
  }
}

async function clearEntries() {
  if (!props.selectedInbox) return;
  try {
    await InstagramMediaReportsAPI.clearPoliticalMapEntries({
      inboxId: props.selectedInbox.id,
    });
    fetchMapEntries();
  } catch {
    // silently fail
  }
}

async function importApproved() {
  if (!props.selectedInbox || !importText.value.trim()) return;
  isImporting.value = true;
  try {
    await InstagramMediaReportsAPI.importPoliticalMap({
      inboxId: props.selectedInbox.id,
      content: importText.value.trim(),
    });
    importText.value = '';
    fetchMapEntries();
  } catch {
    // silently fail
  } finally {
    isImporting.value = false;
  }
}

async function exportMap() {
  if (!props.selectedInbox) return;
  try {
    await InstagramMediaReportsAPI.exportPoliticalMap({
      inboxId: props.selectedInbox.id,
    });
  } catch {
    // silently fail
  }
}

function goToPage(page) {
  if (page < 1 || page > totalPages.value) return;
  currentPage.value = page;
  fetchMapEntries();
}

function loadData() {
  if (props.selectedInbox) {
    currentPage.value = 1;
    fetchMapEntries();
  }
}

watch(() => props.selectedInbox, loadData);

onMounted(loadData);
</script>

<template>
  <div>
    <div
      v-if="!selectedInbox"
      class="flex items-center justify-center py-12 text-n-slate-11"
    >
      {{ $t('INSTAGRAM_MEDIA_REPORTS.TRACKER_SELECT_INBOX') }}
    </div>

    <template v-else>
      <div
        v-if="isLoading"
        class="flex items-center justify-center py-12"
      >
        <Spinner />
      </div>

      <template v-else>
        <div class="flex gap-6">
          <!-- Left column (40%) -->
          <div class="w-2/5 space-y-6">
            <!-- Register identifier -->
            <div class="bg-n-solid-2 rounded-lg p-4 border border-n-weak">
              <h3 class="text-lg font-semibold mb-4 text-n-slate-12">
                {{ $t('INSTAGRAM_MEDIA_REPORTS.CONFIG_REGISTER_TITLE') }}
              </h3>
              <div class="space-y-3">
                <div>
                  <label class="text-xs text-n-slate-11 mb-1 block">
                    {{ $t('INSTAGRAM_MEDIA_REPORTS.CONFIG_TYPE') }}
                  </label>
                  <select
                    v-model="newType"
                    class="w-full py-1.5 px-3 text-sm rounded-lg border bg-n-solid-2 border-n-weak text-n-slate-12"
                  >
                    <option value="adversario">
                      {{ $t('INSTAGRAM_MEDIA_REPORTS.CONFIG_TYPE_ADVERSARY') }}
                    </option>
                    <option value="apoio">
                      {{ $t('INSTAGRAM_MEDIA_REPORTS.CONFIG_TYPE_SUPPORT') }}
                    </option>
                  </select>
                </div>
                <div>
                  <label class="text-xs text-n-slate-11 mb-1 block">
                    {{ $t('INSTAGRAM_MEDIA_REPORTS.CONFIG_PLATFORM') }}
                  </label>
                  <select
                    v-model="newPlatform"
                    class="w-full py-1.5 px-3 text-sm rounded-lg border bg-n-solid-2 border-n-weak text-n-slate-12"
                  >
                    <option value="todas">
                      {{ $t('INSTAGRAM_MEDIA_REPORTS.CONFIG_PLATFORM_ALL') }}
                    </option>
                    <option value="instagram">Instagram</option>
                    <option value="facebook">Facebook</option>
                    <option value="web">Web</option>
                  </select>
                </div>
                <div>
                  <label class="text-xs text-n-slate-11 mb-1 block">
                    {{ $t('INSTAGRAM_MEDIA_REPORTS.CONFIG_FORMAT') }}
                  </label>
                  <select
                    v-model="newFormat"
                    class="w-full py-1.5 px-3 text-sm rounded-lg border bg-n-solid-2 border-n-weak text-n-slate-12"
                  >
                    <option value="@handle">@handle</option>
                    <option value="#hashtag">#hashtag</option>
                    <option value="termo">
                      {{ $t('INSTAGRAM_MEDIA_REPORTS.CONFIG_FORMAT_TERM') }}
                    </option>
                  </select>
                </div>
                <div>
                  <label class="text-xs text-n-slate-11 mb-1 block">
                    {{ $t('INSTAGRAM_MEDIA_REPORTS.CONFIG_VALUE') }}
                  </label>
                  <input
                    v-model="newValue"
                    type="text"
                    class="w-full py-1.5 px-3 text-sm rounded-lg border bg-n-solid-2 border-n-weak text-n-slate-12"
                    :placeholder="$t('INSTAGRAM_MEDIA_REPORTS.CONFIG_VALUE_PLACEHOLDER')"
                  />
                </div>
                <div class="flex justify-end">
                  <button
                    class="px-4 py-1.5 text-sm font-medium rounded-lg bg-n-blue-9 text-white hover:bg-n-blue-10 transition-colors disabled:opacity-50"
                    :disabled="isAdding || !newValue.trim()"
                    @click="addEntry"
                  >
                    <Spinner v-if="isAdding" size="small" />
                    <span v-else>{{ $t('INSTAGRAM_MEDIA_REPORTS.CONFIG_SAVE_BUTTON') }}</span>
                  </button>
                </div>
              </div>
            </div>

            <!-- Action buttons -->
            <div class="flex flex-wrap gap-2">
              <button
                class="px-3 py-1.5 text-xs font-medium rounded-lg border border-n-weak text-n-slate-11 hover:border-n-blue-7 hover:text-n-blue-11 transition-colors"
                @click="generateSuggestions"
              >
                {{ $t('INSTAGRAM_MEDIA_REPORTS.CONFIG_GENERATE_SUGGESTIONS') }}
              </button>
              <button
                class="px-3 py-1.5 text-xs font-medium rounded-lg border border-n-weak text-n-slate-11 hover:border-n-teal-7 hover:text-n-teal-11 transition-colors"
                @click="saveContext"
              >
                {{ $t('INSTAGRAM_MEDIA_REPORTS.CONFIG_SAVE_CONTEXT') }}
              </button>
              <button
                class="px-3 py-1.5 text-xs font-medium rounded-lg border border-n-weak text-n-ruby-11 hover:border-n-ruby-7 transition-colors"
                @click="clearEntries"
              >
                {{ $t('INSTAGRAM_MEDIA_REPORTS.CONFIG_CLEAR') }}
              </button>
            </div>

            <!-- Import section -->
            <div class="bg-n-solid-2 rounded-lg p-4 border border-n-weak">
              <h3 class="text-sm font-semibold mb-3 text-n-slate-12">
                {{ $t('INSTAGRAM_MEDIA_REPORTS.CONFIG_IMPORT_TITLE') }}
              </h3>
              <textarea
                v-model="importText"
                class="w-full py-1.5 px-3 text-sm rounded-lg border bg-n-solid-2 border-n-weak text-n-slate-12 resize-none"
                rows="4"
                :placeholder="$t('INSTAGRAM_MEDIA_REPORTS.CONFIG_IMPORT_PLACEHOLDER')"
              />
              <div class="flex justify-end mt-2">
                <button
                  class="px-4 py-1.5 text-sm font-medium rounded-lg bg-n-teal-9 text-white hover:bg-n-teal-10 transition-colors disabled:opacity-50"
                  :disabled="isImporting || !importText.trim()"
                  @click="importApproved"
                >
                  <Spinner v-if="isImporting" size="small" />
                  <span v-else>{{ $t('INSTAGRAM_MEDIA_REPORTS.CONFIG_IMPORT_BUTTON') }}</span>
                </button>
              </div>
            </div>
          </div>

          <!-- Right column (60%) -->
          <div class="w-3/5">
            <div class="bg-n-solid-2 rounded-lg p-4 border border-n-weak">
              <h3 class="text-lg font-semibold mb-4 text-n-slate-12">
                {{ $t('INSTAGRAM_MEDIA_REPORTS.CONFIG_MAP_TITLE') }}
              </h3>

              <!-- Controls -->
              <div class="flex items-center justify-between mb-4 flex-wrap gap-2">
                <div class="flex items-center gap-2">
                  <button
                    class="px-3 py-1.5 text-xs rounded-lg border border-n-weak text-n-slate-11 hover:border-n-slate-7 transition-colors disabled:opacity-50"
                    :disabled="currentPage <= 1"
                    @click="goToPage(currentPage - 1)"
                  >
                    &laquo;
                  </button>
                  <span class="text-xs text-n-slate-11">
                    {{ currentPage }} / {{ totalPages }}
                  </span>
                  <button
                    class="px-3 py-1.5 text-xs rounded-lg border border-n-weak text-n-slate-11 hover:border-n-slate-7 transition-colors disabled:opacity-50"
                    :disabled="currentPage >= totalPages"
                    @click="goToPage(currentPage + 1)"
                  >
                    &raquo;
                  </button>
                </div>
                <div class="flex items-center gap-2">
                  <button
                    class="px-3 py-1.5 text-xs font-medium rounded-lg border border-n-weak text-n-slate-11 hover:border-n-blue-7 hover:text-n-blue-11 transition-colors"
                    @click="exportMap"
                  >
                    {{ $t('INSTAGRAM_MEDIA_REPORTS.CONFIG_EXPORT') }}
                  </button>
                  <input
                    v-model="searchQuery"
                    type="text"
                    class="py-1.5 px-3 text-sm rounded-lg border bg-n-solid-2 border-n-weak text-n-slate-12 w-48"
                    :placeholder="$t('INSTAGRAM_MEDIA_REPORTS.CONFIG_SEARCH_PLACEHOLDER')"
                  />
                </div>
              </div>

              <!-- Table -->
              <div v-if="filteredEntries.length" class="overflow-x-auto">
                <table class="w-full text-sm">
                  <thead>
                    <tr class="border-b border-n-weak">
                      <th class="text-left py-2 px-3 text-xs font-medium text-n-slate-11">#</th>
                      <th class="text-left py-2 px-3 text-xs font-medium text-n-slate-11">
                        {{ $t('INSTAGRAM_MEDIA_REPORTS.CONFIG_COL_VALUE') }}
                      </th>
                      <th class="text-center py-2 px-3 text-xs font-medium text-n-slate-11">
                        {{ $t('INSTAGRAM_MEDIA_REPORTS.CONFIG_COL_ORIGIN') }}
                      </th>
                      <th class="text-center py-2 px-3 text-xs font-medium text-n-slate-11">
                        {{ $t('INSTAGRAM_MEDIA_REPORTS.CONFIG_COL_VALIDATION') }}
                      </th>
                      <th class="text-left py-2 px-3 text-xs font-medium text-n-slate-11">
                        {{ $t('INSTAGRAM_MEDIA_REPORTS.CONFIG_COL_SOURCE') }}
                      </th>
                      <th class="text-center py-2 px-3 text-xs font-medium text-n-slate-11">
                        {{ $t('INSTAGRAM_MEDIA_REPORTS.CONFIG_COL_ACTIONS') }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="entry in filteredEntries"
                      :key="entry.id"
                      class="border-b border-n-weak"
                    >
                      <td class="py-2 px-3 text-n-slate-11 text-xs">
                        {{ idPrefix(entry) }}{{ entry.id }}
                      </td>
                      <td class="py-2 px-3 text-n-blue-11 font-medium text-xs">
                        {{ entry.value }}
                      </td>
                      <td class="py-2 px-3 text-center">
                        <span
                          class="text-xs px-2 py-0.5 rounded-full"
                          :class="entry.origin === 'ia' ? 'bg-n-violet-3 text-n-violet-11' : 'bg-n-slate-3 text-n-slate-11'"
                        >
                          {{ entry.origin || 'manual' }}
                        </span>
                      </td>
                      <td class="py-2 px-3 text-center">
                        <span
                          v-if="entry.validated"
                          class="text-n-teal-11"
                        >
                          &#x2713;
                        </span>
                        <span v-else class="text-n-slate-9">-</span>
                      </td>
                      <td class="py-2 px-3 text-n-slate-11 text-xs">
                        {{ entry.source || '-' }}
                      </td>
                      <td class="py-2 px-3 text-center">
                        <div class="flex items-center justify-center gap-1">
                          <button
                            v-if="!entry.validated"
                            class="px-2 py-1 text-xs rounded-lg border border-n-weak text-n-teal-11 hover:border-n-teal-7 transition-colors"
                            @click="validateEntry(entry.id)"
                          >
                            &#x2713;
                          </button>
                          <button
                            class="px-2 py-1 text-xs rounded-lg border border-n-weak text-n-ruby-11 hover:border-n-ruby-7 transition-colors"
                            @click="removeEntry(entry.id)"
                          >
                            &#x2715;
                          </button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div v-else class="flex items-center justify-center py-8 text-n-slate-11">
                {{ $t('INSTAGRAM_MEDIA_REPORTS.CONFIG_NO_ENTRIES') }}
              </div>
            </div>
          </div>
        </div>
      </template>
    </template>
  </div>
</template>
